import { MarkdownParagraph } from './paragraph';

const tocPlaceholder = '((())) TABLE_OF_CONTENTS ((()))';

export type TMDTocProps = {
  title?: string;
  minLevel?: number;
  maxLevel?: number;
  filter?: (el: HTMLElement) => boolean;
};

export class MarkdownTableOfContents extends MarkdownParagraph {
  public constructor() {
    super(tocPlaceholder);
  }

  /**
   * Este método toma el html, busca todos los headings (que ya fueron
   * procesados por setUniqueIds) y crea una tabla de contenidos a partir de
   * esos ids
   */
  private static makeTableOfContents(props: TMDTocProps, html: string) {
    const idMap: Record<string, boolean> = {};
    const parser: DOMParser = new DOMParser();

    const parsedDocument: Document = parser.parseFromString(html, 'text/html');

    const headers: NodeListOf<HTMLHeadElement> =
      parsedDocument.querySelectorAll(
        new Array((props.maxLevel || 3) - (props.minLevel || 1) + 1)
          .fill(1)
          .map((_, i) => `h${i + (props.minLevel || 1)}`)
          .join(', '),
      );

    const root: HTMLUListElement = document.createElement('ul');
    root.id = 'TableOfContents';

    const prevParentMap: Record<number, HTMLElement> = { 0: root };

    for (const header of headers) {
      if (!props.filter || props.filter(header)) {
        // eslint-disable-next-line no-param-reassign
        this.setUniqueId(idMap, header);

        const depth = this.getNodeDepth(header);
        const parentDepth = depth - 1;

        this.deletePrevValues(prevParentMap, depth);

        const ulParent = this.getParent(prevParentMap, parentDepth);

        this.createNewLevel(header, ulParent, prevParentMap, parentDepth);
      }
    }

    return parsedDocument.documentElement.outerHTML.replace(
      tocPlaceholder,
      `<h2>${props.title}</h2>${root.outerHTML}`,
    );
  }

  private static deletePrevValues(
    prevParentMap: Record<number, HTMLElement | null>,
    depth: number,
  ): void {
    // eslint-disable-next-line no-param-reassign
    for (let i = depth; i < 7; i++) prevParentMap[i] = null;
  }

  private static getParent(
    prevParentMap: Record<number, HTMLElement>,
    depth: number,
  ): HTMLElement {
    // eslint-disable-next-line no-param-reassign
    for (let i = depth; i >= 0; i--) {
      const parent = prevParentMap[i];
      if (parent) return parent;
    }
    throw new Error('Element has no parent');
  }

  private static createNewLevel(
    elem: HTMLElement,
    parentElement: HTMLElement,
    prevParentMap: Record<number, HTMLElement>,
    parentDepth: number,
  ) {
    const newLi = document.createElement('li');
    const newA = document.createElement('a');
    const newB = document.createElement('b');
    newA.href = `#${elem.id}`;
    newB.appendChild(document.createTextNode(elem.textContent as string));
    newA.appendChild(newB);
    newLi.appendChild(newA);
    parentElement.appendChild(newLi);
    // eslint-disable-next-line no-param-reassign
    prevParentMap[parentDepth + 1] = document.createElement('ul');
    newLi.appendChild(prevParentMap[parentDepth + 1]);
  }

  private static getNodeDepth(node: HTMLElement): number {
    return parseInt(node.nodeName.replace('H', ''));
  }

  /**
   * Este método toma el html original, busca todos los headings y a cada uno
   * le pone un id único, reemplazando el id original si lo tuviera:
   *
   * Si un heading no tiene id: le agrega uno autogenerado con autoincrement +
   * texto del heading procesado (reemplazar espacios por guiones, etc)
   *
   * Si un heading tiene id: le agrega un autoincrement con un sufijo:
   * {id_original}_-_{autoincrement}
   *
   * De esta forma, nos aseguramos que ningún heading tenga un id igual a otro
   */
  private static setUniqueId(
    idMap: Record<string, boolean>,
    element: HTMLHeadElement,
  ) {
    const replacementRegex = /[\s()]+/g;
    const replacementChar = '-';

    const id = (element.id ? element.id : element.textContent!)
      ?.toLocaleLowerCase()
      .trim()
      .replaceAll(replacementRegex, replacementChar);

    const tokens: (string | number)[] = id.split('_-_', 2);
    if (tokens.length !== 2) tokens[1] = 0;
    let cond = true;
    for (
      ;
      cond;
      // eslint-disable-next-line no-param-reassign
      (idMap[`${tokens[0]}_-_${tokens[1]}`] = true) && (tokens[1] as number)++
    )
      cond = idMap[`${tokens[0]}_-_${tokens[1]}`];

    // eslint-disable-next-line no-param-reassign
    element.id = `${tokens[0]}_-_${tokens[1]}`;
  }

  public static appendTableOfContents(
    props: TMDTocProps,
    html: string,
  ): string {
    return this.makeTableOfContents(props, html);
  }
}
